import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { EmptyState } from '@/components/common/EmptyState'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Quote = Tables<'quotes'>

export function QuotesListPage() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')

  useEffect(() => {
    if (!profile?.organization_id) return
    async function load() {
      let query = supabase.from('quotes').select('*')
        .eq('organization_id', profile!.organization_id!)
        .order('created_at', { ascending: false })
      if (statusFilter !== 'all') query = query.eq('status', statusFilter as Tables<'quotes'>['status'])
      const { data } = await query
      setQuotes(data || [])
      setLoading(false)
    }
    load()
  }, [profile, statusFilter])

  return (
    <div>
      <PageHeader title="Quotes" description="Manage all quotations" actions={<Link to="/gaviota/quotes/new" className={css.btnPrimary}>New Quote</Link>} />

      <div className={css.tabs} style={{ marginBottom: 'var(--space-6)' }}>
        {['all', 'draft', 'sent', 'accepted', 'rejected', 'converted'].map(s => (
          <button key={s} className={`${css.tab} ${statusFilter === s ? css.tabActive : ''}`} onClick={() => setStatusFilter(s)}>
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p> : quotes.length === 0 ? (
        <EmptyState title="No quotes found" description="Create your first quote to get started" action={<Link to="/gaviota/quotes/new" className={css.btnPrimary}>New Quote</Link>} />
      ) : (
        <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
          <table className={css.table}>
            <thead><tr><th>Reference</th><th>Version</th><th>Status</th><th>Total</th><th>Valid Until</th><th>Created</th></tr></thead>
            <tbody>
              {quotes.map(q => (
                <tr key={q.id} className={css.tableClickableRow} onClick={() => navigate(`/gaviota/quotes/${q.id}`)}>
                  <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{q.reference_code}</code></td>
                  <td>v{q.version}</td>
                  <td><StatusBadge label={q.status} /></td>
                  <td style={{ fontWeight: 500 }}>£{Number(q.total).toLocaleString()}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{q.valid_until ? new Date(q.valid_until).toLocaleDateString() : '-'}</td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>{new Date(q.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
